import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Calendar,
  Thermometer,
  Grape,
  Award,
  Trash2,
  CheckCircle,
  Star,
  Edit3,
  ZoomIn,
  X,
  GlassWater,
  Lock,
  Users,
  Eye,
  AlertCircle,
  Share2,
  Archive,
  Hourglass,
  ArrowLeftRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
import { useWines } from '@/contexts/WinesContext';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { AppIcon } from '@/components/icons/AppIcon';
import { DeleteConfirmModal } from '@/components/admin/DeleteConfirmModal';
import { ShareWinesDialog } from '@/components/wine/ShareWinesDialog';
import { SelectConnectedUsersDialog } from '@/components/wine/SelectConnectedUsersDialog';
import type { WineType } from '@/types';

const wineTypeLabels: Record<WineType, string> = {
  red: 'Rosso',
  white: 'Bianco',
  rose: 'Rosato',
  sparkling: 'Spumante',
  dessert: 'Dessert',
  fortified: 'Fortificato',
  orange: 'Arancione',
  whisky: 'Whisky',
  rum: 'Rum',
  gin: 'Gin',
  grappa: 'Grappa',
  cognac: 'Cognac',
  liqueur: 'Liquore',
  other: 'Altro',
};

const wineTypeColors: Record<WineType, string> = {
  // Vini - Palette naturale e sofisticata con sfondo pieno
  red: 'bg-wine-burgundy text-white border border-wine-burgundy',
  white: 'bg-amber-100 text-amber-900 border border-amber-300',
  rose: 'bg-wine-rose text-white border border-wine-rose',
  sparkling: 'bg-sky-100 text-sky-900 border border-sky-300',
  dessert: 'bg-amber-200 text-amber-950 border border-amber-400',
  fortified: 'bg-wine-merlot text-white border border-wine-merlot',
  orange: 'bg-orange-100 text-orange-900 border border-orange-300',

  // Spirits - Palette terra e legno con sfondo pieno
  whisky: 'bg-wine-amber text-wine-barrel border border-wine-oak',
  rum: 'bg-amber-200 text-amber-950 border border-amber-400',
  gin: 'bg-emerald-100 text-emerald-900 border border-emerald-300',
  grappa: 'bg-stone-200 text-stone-900 border border-stone-400',
  cognac: 'bg-wine-oak text-white border border-wine-barrel',
  liqueur: 'bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-300',

  other: 'bg-gray-200 text-gray-900 border border-gray-400',
};

export const WineDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wines, deleteWine, markAsConsumed, canModifyWine, canDeleteWine, isOwner, generateShareCode, unshareWine, updateShareAccessType, connectedUsers, locations } = useWines();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConsumedModal, setShowConsumedModal] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showSelectUsersDialog, setShowSelectUsersDialog] = useState(false);
  const [showUnshareModal, setShowUnshareModal] = useState(false);
  const [userToUnshare, setUserToUnshare] = useState<{ userId: string; userName: string } | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [rating, setRating] = useState(5);
  const [consumedNotes, setConsumedNotes] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [marking, setMarking] = useState(false);
  const [unsharing, setUnsharing] = useState(false);
  const [togglingAccessUserId, setTogglingAccessUserId] = useState<string | null>(null);

  // GSAP Refs
  const badgesRef = useRef<HTMLDivElement>(null);
  const infoGridRef = useRef<HTMLDivElement>(null);
  const ratingStarsRef = useRef<HTMLDivElement>(null);
  const detailsCardRef = useRef<HTMLDivElement>(null);
  const tastingNotesCardRef = useRef<HTMLDivElement>(null);
  const pairingsCardRef = useRef<HTMLDivElement>(null);
  const personalNotesCardRef = useRef<HTMLDivElement>(null);
  const sharingCardRef = useRef<HTMLDivElement>(null);

  const wine = useMemo(() => wines.find((w) => w.id === id), [wines, id]);

  // Array di tutte le immagini disponibili
  const wineImages = useMemo(() => {
    if (!wine) return [];
    return wine.imageUrls || (wine.imageUrl ? [wine.imageUrl] : []);
  }, [wine]);

  // Calcola permessi
  const permissions = useMemo(() => {
    if (!wine || !user) return { canEdit: false, canDelete: false, canConsume: false, isWineOwner: false };
    return {
      canEdit: canModifyWine(wine),
      canDelete: canDeleteWine(wine),
      canConsume: wine.status === 'cellar' && canModifyWine(wine),
      isWineOwner: isOwner(wine),
    };
  }, [wine, user, canModifyWine, canDeleteWine, isOwner]);

  // Accesso dell'utente corrente (se condiviso)
  const myAccess = useMemo(() => {
    if (!wine || !user) return null;
    return wine.sharedWith?.find(s => s.userId === user.id) || null;
  }, [wine, user]);

  // Nome del proprietario (con fallback per vini già condivisi)
  const ownerDisplayName = useMemo(() => {
    if (!wine) return 'Proprietario';

    // Se esiste ownerName, usalo
    if (wine.ownerName) return wine.ownerName;

    // Altrimenti cerca il proprietario nelle connessioni
    const ownerConnection = connectedUsers.find(
      c => (c.userA === wine.ownerId || c.userB === wine.ownerId) && c.status === 'accepted'
    );

    if (ownerConnection) {
      return ownerConnection.userA === wine.ownerId
        ? ownerConnection.userAName || ownerConnection.userAEmail || 'Proprietario'
        : ownerConnection.userBName || ownerConnection.userBEmail || 'Proprietario';
    }

    return 'Proprietario';
  }, [wine, connectedUsers]);

  // Scroll to top when the page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // GSAP Animations - Badges stagger
  useEffect(() => {
    if (badgesRef.current && wine) {
      const badges = badgesRef.current.querySelectorAll('.badge-item');
      gsap.fromTo(
        badges,
        { opacity: 0, scale: 0.5, y: -10 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.1,
          ease: 'back.out(1.7)',
        }
      );
    }
  }, [wine?.id]);

  // GSAP Animations - Info Grid stagger
  useEffect(() => {
    if (infoGridRef.current && wine) {
      const items = infoGridRef.current.querySelectorAll('.info-item');
      gsap.fromTo(
        items,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power2.out',
          delay: 0.3,
        }
      );
    }
  }, [wine?.id]);

  // GSAP Animations - Rating Stars progressive fill
  useEffect(() => {
    if (ratingStarsRef.current && wine?.rating) {
      const stars = ratingStarsRef.current.querySelectorAll('.star-item');
      gsap.fromTo(
        stars,
        { scale: 0, rotation: -180 },
        {
          scale: 1,
          rotation: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'back.out(1.7)',
          delay: 0.5,
        }
      );
    }
  }, [wine?.rating]);

  // GSAP Animations - ScrollTrigger for cards
  useEffect(() => {
    if (!wine) return;

    const cards = [
      detailsCardRef.current,
      tastingNotesCardRef.current,
      pairingsCardRef.current,
      personalNotesCardRef.current,
      sharingCardRef.current,
    ].filter(Boolean);

    cards.forEach((card) => {
      if (card) {
        gsap.fromTo(
          card,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
          }
        );
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [wine?.id]);

  if (!wine) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AppIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Vino non trovato</h2>
          <p className="text-gray-600 mb-6">Il vino che stai cercando non esiste.</p>
          <Button onClick={() => navigate('/')}>Torna alla Cantina</Button>
        </div>
      </Layout>
    );
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteWine(wine.id);
      navigate('/');
    } catch (error) {
      console.error('Error deleting wine:', error);
      setDeleting(false);
    }
  };

  const handleMarkAsConsumed = async () => {
    setMarking(true);
    try {
      await markAsConsumed(wine.id, rating, consumedNotes);
      setShowConsumedModal(false);
    } catch (error) {
      console.error('Error marking as consumed:', error);
    } finally {
      setMarking(false);
    }
  };

  const handleUnshare = async () => {
    if (!userToUnshare) return;

    setUnsharing(true);
    try {
      await unshareWine(wine.id, userToUnshare.userId);
      setShowUnshareModal(false);
      setUserToUnshare(null);
    } catch (error) {
      console.error('Error unsharing wine:', error);
      alert('Errore durante la rimozione della condivisione');
    } finally {
      setUnsharing(false);
    }
  };

  const handleToggleAccessType = async (userId: string, currentType: 'full' | 'read-only') => {
    setTogglingAccessUserId(userId);
    try {
      const newType = currentType === 'full' ? 'read-only' : 'full';
      await updateShareAccessType(wine.id, userId, newType);
    } catch (error) {
      console.error('Error toggling access type:', error);
      alert('Errore durante la modifica dell\'accesso');
    } finally {
      setTogglingAccessUserId(null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'In attesa...';

    // Controlla se è un Timestamp Firestore valido con il metodo toDate
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      try {
        const date = timestamp.toDate();
        return new Intl.DateTimeFormat('it-IT', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }).format(date);
      } catch (error) {
        return 'In attesa...';
      }
    }

    // Fallback per altri formati di data
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat('it-IT', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }).format(date);
      }
    } catch (error) {
      // Ignora errori
    }

    return 'In attesa...';
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <Button
            variant="ghost"
            icon={<ChevronLeft className="w-5 h-5" />}
            onClick={() => navigate('/')}
          >
            <span className="hidden sm:inline">Indietro</span>
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={permissions.canEdit ? <Edit3 className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
              onClick={() => navigate(`/edit/${wine.id}`)}
              disabled={!permissions.canEdit}
              title={!permissions.canEdit ? 'Non hai i permessi per modificare questo vino' : undefined}
            >
              <span className="hidden sm:inline">Modifica</span>
            </Button>
            <Button
              variant="outline"
              icon={permissions.isWineOwner ? <Share2 className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
              onClick={() => setShowSelectUsersDialog(true)}
              disabled={!permissions.isWineOwner || wine.status === 'consumed'}
              title={
                !permissions.isWineOwner
                  ? 'Solo il proprietario può condividere questo vino'
                  : wine.status === 'consumed'
                  ? 'Non puoi condividere vini bevuti'
                  : undefined
              }
            >
              <span className="hidden sm:inline">Condividi</span>
            </Button>
            <Button
              variant="danger"
              icon={permissions.canDelete ? <Trash2 className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
              onClick={() => setShowDeleteModal(true)}
              disabled={!permissions.canDelete}
              title={!permissions.canDelete ? 'Non hai i permessi per eliminare questo vino' : undefined}
            >
              <span className="hidden sm:inline">Elimina</span>
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Main Info Card */}
          <Card padding="none">
            {/* Wine Images Carousel - Swipeable */}
            {wineImages.length > 0 ? (
              <div className="relative bg-gray-50 rounded-t-lg">
                {/* Carosello immagini con scroll-snap */}
                <div
                  className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                  onScroll={(e) => {
                    const target = e.currentTarget;
                    const scrollLeft = target.scrollLeft;
                    const itemWidth = target.offsetWidth;
                    const index = Math.round(scrollLeft / itemWidth);
                    setCurrentImageIndex(index);
                  }}
                >
                  {wineImages.map((img, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 w-full snap-center"
                    >
                      <div
                        className="relative cursor-pointer group h-[280px] sm:h-[320px] md:h-[360px] flex items-center justify-center px-2 sm:px-0"
                        onClick={() => setFullscreenImage(img)}
                      >
                        <img
                          src={img}
                          alt={`${wine.name} - foto ${index + 1}`}
                          className="w-full h-auto sm:w-auto sm:h-full object-contain mx-auto max-h-full"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <ZoomIn className="w-10 h-10 text-white drop-shadow-lg" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Indicatore di posizione e frecce di navigazione */}
                {wineImages.length > 1 && (
                  <>
                    {/* Counter centrale */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-medium">
                      {currentImageIndex + 1} / {wineImages.length}
                    </div>

                    {/* Dots indicator in basso */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {wineImages.map((_, index) => (
                        <div
                          key={index}
                          className={`h-1.5 rounded-full transition-all ${
                            index === currentImageIndex
                              ? 'w-6 bg-white'
                              : 'w-1.5 bg-white/50'
                          }`}
                        />
                      ))}
                    </div>

                    {/* Frecce di navigazione (solo desktop) */}
                    {currentImageIndex > 0 && (
                      <button
                        onClick={() => {
                          const container = document.querySelector('.overflow-x-auto');
                          if (container) {
                            container.scrollTo({
                              left: (currentImageIndex - 1) * container.clientWidth,
                              behavior: 'smooth'
                            });
                          }
                        }}
                        className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                    )}

                    {currentImageIndex < wineImages.length - 1 && (
                      <button
                        onClick={() => {
                          const container = document.querySelector('.overflow-x-auto');
                          if (container) {
                            container.scrollTo({
                              left: (currentImageIndex + 1) * container.clientWidth,
                              behavior: 'smooth'
                            });
                          }
                        }}
                        className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-64 md:h-96 bg-gradient-to-br from-wine-burgundy/10 to-wine-burgundy/5 rounded-t-lg flex items-center justify-center">
                <AppIcon className="w-32 h-32 opacity-30" />
              </div>
            )}

            <div className="p-6">
              {/* Type Badge */}
              <div ref={badgesRef} className="flex gap-2 mb-4">
                <span
                  className={`badge-item px-3 py-1 rounded-full text-xs font-semibold ${wineTypeColors[wine.type]}`}
                >
                  {wineTypeLabels[wine.type]}
                </span>
              </div>

              {/* Wine Name & Producer */}
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 mb-2 mt-4">{wine.name}</h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-4">{wine.producer}</p>

              {/* Quick Info Grid */}
              <div ref={infoGridRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 py-4 border-y border-gray-200">
                {wine.vintage && (
                  <div className="info-item">
                    <p className="text-sm text-gray-600">Annata</p>
                    <p className="text-lg font-bold text-gray-900">{wine.vintage}</p>
                  </div>
                )}
                {wine.region && (
                  <div className="info-item">
                    <p className="text-sm text-gray-600">Regione</p>
                    <p className="text-lg font-bold text-gray-900">{wine.region}</p>
                  </div>
                )}
                {wine.alcoholContent && (
                  <div className="info-item">
                    <p className="text-sm text-gray-600">Gradazione</p>
                    <p className="text-lg font-bold text-gray-900">{wine.alcoholContent}%</p>
                  </div>
                )}
                {(wine.estimatedValue || wine.purchasePrice) && (
                  <div className="info-item">
                    <p className="text-sm text-gray-600">
                      {wine.purchasePrice && wine.estimatedValue ? 'Valore / Acquisto' : wine.purchasePrice ? 'Prezzo Acquisto' : 'Valore Stimato'}
                    </p>
                    <div className="flex items-center gap-2">
                      {wine.estimatedValue && (
                        <p className="text-lg font-bold text-wine-purple">€{wine.estimatedValue.toFixed(2)}</p>
                      )}
                      {wine.purchasePrice && wine.estimatedValue && (
                        <span className="text-gray-400">/</span>
                      )}
                      {wine.purchasePrice && (
                        <p className="text-lg font-bold text-green-600">€{wine.purchasePrice.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                )}
                {wine.status === 'cellar' && wine.quantity && wine.quantity > 1 && (
                  <div className="info-item">
                    <p className="text-sm text-gray-600">Quantità</p>
                    <p className="text-lg font-bold text-gray-900">{wine.quantity} bottiglie</p>
                  </div>
                )}
              </div>

              {/* Rating (if consumed) */}
              {wine.status === 'consumed' && wine.rating && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">La tua valutazione</p>
                    <div ref={ratingStarsRef} className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`star-item w-6 h-6 ${
                            i < wine.rating! ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-lg font-bold text-gray-900">
                        {wine.rating}/5
                      </span>
                    </div>
                  </div>

                  {/* Consumed Notes (tasting notes when marked as consumed) */}
                  {wine.consumedNotes && (
                    <div className="pt-3 border-t border-yellow-100">
                      <p className="text-sm text-gray-600 mb-1">Note di degustazione</p>
                      <p className="text-gray-700 leading-relaxed">{wine.consumedNotes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Mark as Consumed Button */}
              {wine.status === 'cellar' && (
                <div className="mt-4">
                  <Button
                    fullWidth
                    icon={permissions.canConsume ? <CheckCircle className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                    onClick={() => setShowConsumedModal(true)}
                    disabled={!permissions.canConsume}
                    title={!permissions.canConsume ? 'Non hai i permessi per segnare questo vino come bevuto' : undefined}
                  >
                    {wine.quantity && wine.quantity > 1
                      ? `Segna una bottiglia come bevuta (${wine.quantity} disponibili)`
                      : 'Segna come Bevuto'}
                  </Button>
                </div>
              )}

              {/* Consumed Date */}
              {wine.status === 'consumed' && wine.consumedDate && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Bevuto il</p>
                  <p className="text-lg font-bold text-gray-900">{formatDate(wine.consumedDate)}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Details Card */}
          <div ref={detailsCardRef}>
          <Card>
            <CardHeader>
              <CardTitle>Dettagli</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {wine.grape && (
                  <DetailRow icon={<Grape className="w-5 h-5" />} label="Vitigno" value={wine.grape} />
                )}
                {wine.denomination && (
                  <DetailRow
                    icon={<Award className="w-5 h-5" />}
                    label="Denominazione"
                    value={wine.denomination}
                  />
                )}
                {wine.servingTemperature && (
                  <DetailRow
                    icon={<Thermometer className="w-5 h-5" />}
                    label="Temperatura Servizio"
                    value={wine.servingTemperature}
                  />
                )}
                {wine.glassType && (
                  <DetailRow
                    icon={<GlassWater className="w-5 h-5" />}
                    label="Tipo di Bicchiere"
                    value={wine.glassType}
                  />
                )}
                {/* Location display - combine position and notes if both exist */}
                {(wine.locationId || wine.location) && (
                  <div className="flex items-center gap-3">
                    <div className="text-wine-purple">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">Posizione</p>
                      {wine.locationId && (
                        <p className="text-base font-medium text-gray-900">
                          {(() => {
                            const locationName = locations.find(l => l.id === wine.locationId)?.name;
                            if (locationName) {
                              return locationName;
                            }
                            // Se non troviamo la posizione, è del proprietario
                            return isOwner(wine) ? 'Posizione non trovata' : 'Posizione del proprietario';
                          })()}
                        </p>
                      )}
                      {wine.location && (
                        <p className={`text-sm ${wine.locationId ? 'text-gray-600 mt-1' : 'text-base font-medium text-gray-900'}`}>
                          {wine.location}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <DetailRow
                  icon={<Calendar className="w-5 h-5" />}
                  label="Aggiunto il"
                  value={formatDate(wine.createdAt)}
                />
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Tasting Notes */}
          {wine.tastingNotes && (
            <div ref={tastingNotesCardRef}>
            <Card>
              <CardHeader>
                <CardTitle>Note di Degustazione</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">{wine.tastingNotes}</p>
              </CardContent>
            </Card>
            </div>
          )}

          {/* Pairings */}
          {wine.pairings && wine.pairings.length > 0 && (
            <div ref={pairingsCardRef}>
            <Card>
              <CardHeader>
                <CardTitle>Abbinamenti Consigliati</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {wine.pairings.map((pairing, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-wine-purple/10 text-wine-purple rounded-full text-sm font-medium"
                    >
                      {pairing}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
            </div>
          )}

          {/* Storage & Aging */}
          {(wine.storageTemperature || wine.storageNotes) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="w-5 h-5" />
                  Conservazione
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {wine.storageTemperature && (
                    <div className="flex items-start gap-3">
                      <Thermometer className="w-5 h-5 text-wine-purple flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Temperatura ideale</p>
                        <p className="text-base text-gray-900">{wine.storageTemperature}</p>
                      </div>
                    </div>
                  )}
                  {wine.storageNotes && (
                    <div className="flex items-start gap-3">
                      <Archive className="w-5 h-5 text-wine-purple flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Condizioni</p>
                        <p className="text-base text-gray-900">{wine.storageNotes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Consumption and Aging Info */}
          {(wine.drinkByYear || wine.agingPotential) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Consumo e Invecchiamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {wine.drinkByYear && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-wine-purple flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Da bere entro</p>
                        <p className="text-base text-gray-900">{wine.drinkByYear}</p>
                      </div>
                    </div>
                  )}
                  {wine.agingPotential && (
                    <div className="flex items-start gap-3">
                      <Hourglass className="w-5 h-5 text-wine-purple flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Potenziale di invecchiamento</p>
                        <p className="text-base text-gray-900">{wine.agingPotential}</p>
                      </div>
                    </div>
                  )}

                  {/* Alert for wines past their drinking window */}
                  {wine.drinkByYear && new Date().getFullYear() > wine.drinkByYear && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 p-3 bg-orange-50 border-l-4 border-orange-400 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-orange-900">
                            Finestra di consumo ottimale superata
                          </p>
                          <p className="text-xs text-orange-700 mt-1">
                            Questo vino era consigliato per essere consumato entro il {wine.drinkByYear}.
                            Potrebbe aver superato il suo picco qualitativo.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personal Notes */}
          {wine.personalNotes && (
            <div ref={personalNotesCardRef}>
            <Card>
              <CardHeader>
                <CardTitle>Note Personali</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">{wine.personalNotes}</p>
              </CardContent>
            </Card>
            </div>
          )}

          {/* Sharing Section Card */}
          {(wine.isShared || myAccess) && (
            <div ref={sharingCardRef}>
            <Card>
              <CardHeader>
                <CardTitle>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Condivisione
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Se sono owner */}
                {permissions.isWineOwner ? (
                  <div className="space-y-4">
                    {wine.sharedWith && wine.sharedWith.length > 0 ? (
                      <>
                        <p className="text-sm text-gray-600">Questo vino è condiviso con:</p>
                        <div className="space-y-3">
                          {wine.sharedWith.map((share) => (
                            <div
                              key={share.userId}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-wine-purple/10 flex items-center justify-center">
                                  <Users className="w-5 h-5 text-wine-purple" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{share.userName}</p>
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                                      share.type === 'full'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                  >
                                    {share.type === 'full' ? (
                                      <>
                                        <Users className="w-3 h-3" />
                                        <span className="max-[425px]:hidden">Accesso completo</span>
                                      </>
                                    ) : (
                                      <>
                                        <Eye className="w-3 h-3" />
                                        <span className="max-[425px]:hidden">Solo lettura</span>
                                      </>
                                    )}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={<ArrowLeftRight className="w-4 h-4" />}
                                  onClick={() => handleToggleAccessType(share.userId, share.type)}
                                  disabled={togglingAccessUserId === share.userId}
                                  loading={togglingAccessUserId === share.userId}
                                >
                                  <span className="hidden md:inline">
                                    {share.type === 'full' ? 'Passa a Lettura' : 'Passa a Completo'}
                                  </span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  icon={<Trash2 className="w-4 h-4" />}
                                  onClick={() => {
                                    setUserToUnshare({ userId: share.userId, userName: share.userName });
                                    setShowUnshareModal(true);
                                  }}
                                >
                                  <span className="hidden md:inline">Rimuovi</span>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button
                          variant="outline"
                          icon={<Users className="w-5 h-5" />}
                          onClick={() => setShowSelectUsersDialog(true)}
                          fullWidth
                        >
                          Condividi con altri
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-600 mb-4">Questo vino non è ancora condiviso</p>
                        <Button
                          variant="outline"
                          icon={<Users className="w-5 h-5" />}
                          onClick={() => setShowSelectUsersDialog(true)}
                        >
                          Condividi questo vino
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Se NON sono owner */
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-wine-purple/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-wine-purple" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Proprietario</p>
                        <p className="font-medium text-gray-900">
                          {ownerDisplayName}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-2">Il tuo accesso</p>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-semibold ${
                          myAccess?.type === 'full'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {myAccess?.type === 'full' ? (
                          <>
                            <Users className="w-4 h-4" />
                            Accesso completo
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4" />
                            Solo lettura
                          </>
                        )}
                      </span>
                    </div>

                    {myAccess?.type === 'read-only' && (
                      <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">Accesso in sola lettura</p>
                          <p className="text-sm text-blue-700 mt-1">
                            Non puoi modificare o bere questo vino. Solo il proprietario può farlo.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Elimina Vino"
          message={`Sei sicuro di voler eliminare "${wine.name}"? Questa azione è irreversibile.`}
          loading={deleting}
        />

        {/* Unshare Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showUnshareModal}
          onClose={() => {
            setShowUnshareModal(false);
            setUserToUnshare(null);
          }}
          onConfirm={handleUnshare}
          title="Rimuovi Condivisione"
          message={`Sei sicuro di voler rimuovere la condivisione di "${wine.name}" con ${userToUnshare?.userName}? L'utente perderà immediatamente l'accesso a questo vino.`}
          loading={unsharing}
        />

        {/* Mark as Consumed Modal */}
        <AnimatePresence>
          {showConsumedModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowConsumedModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-lg p-6 max-w-md w-full"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {wine.quantity && wine.quantity > 1 ? 'Segna una bottiglia come bevuta' : 'Segna come Bevuto'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {wine.quantity && wine.quantity > 1
                    ? `Stai per segnare 1 bottiglia come bevuta. Rimarranno ${wine.quantity - 1} in cantina.`
                    : 'Come valuteresti questo vino? Aggiungi le tue note di degustazione.'}
                </p>

                {/* Rating */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valutazione
                  </label>
                  <div className="flex gap-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setRating(i + 1)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note di Degustazione
                  </label>
                  <textarea
                    value={consumedNotes}
                    onChange={(e) => setConsumedNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wine-purple focus:border-transparent"
                    placeholder="Com'era? Cosa ti è piaciuto? Con cosa l'hai abbinato?"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowConsumedModal(false)}
                    fullWidth
                  >
                    Annulla
                  </Button>
                  <Button
                    icon={<CheckCircle className="w-5 h-5" />}
                    onClick={handleMarkAsConsumed}
                    loading={marking}
                    fullWidth
                  >
                    Conferma
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Share Wine Dialog */}
        <ShareWinesDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          wines={wine ? [wine] : []}
          onShare={async (wineIds, type) => {
            const code = await generateShareCode(wineIds, type);
            return code;
          }}
        />

        <SelectConnectedUsersDialog
          isOpen={showSelectUsersDialog}
          onClose={() => setShowSelectUsersDialog(false)}
          wineIds={wine ? [wine.id] : []}
          wineNames={wine ? [wine.name] : []}
        />

        {/* Fullscreen Image Modal */}
        <AnimatePresence>
          {fullscreenImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
              onClick={() => setFullscreenImage(null)}
            >
              {/* Close button */}
              <button
                onClick={() => setFullscreenImage(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Image counter */}
              {wineImages.length > 1 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
                  {currentImageIndex + 1} / {wineImages.length}
                </div>
              )}

              {/* Previous button */}
              {wineImages.length > 1 && currentImageIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = currentImageIndex - 1;
                    setCurrentImageIndex(newIndex);
                    setFullscreenImage(wineImages[newIndex]);
                  }}
                  className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
              )}

              {/* Next button */}
              {wineImages.length > 1 && currentImageIndex < wineImages.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = currentImageIndex + 1;
                    setCurrentImageIndex(newIndex);
                    setFullscreenImage(wineImages[newIndex]);
                  }}
                  className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              )}

              {/* Main image */}
              <motion.img
                key={fullscreenImage}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                src={fullscreenImage}
                alt={wine.name}
                className="max-w-full max-h-full object-contain p-4"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, value }) => {
  return (
    <div className="flex items-center gap-3">
      <div className="text-wine-purple">{icon}</div>
      <div className="flex-1">
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-base font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
};
